import { ChangeEvent, useState, useEffect, useMemo, useCallback } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwHierarchicalCategorySelector from "@components/shared/OwHierarchicalCategorySelector/OwHierarchicalCategorySelector";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import AveryTemplateEnum from "@models/AveryTemplateEnum";
import IBarcodeLabelDTO from "@models/DTO/IBarcodeLabelDTO";
import ICatalog_Item from "@models/ICatalog_Item";
import ICatalog from "@models/ICatalog";
import ICategory from "@models/ICategory";
import IPrice_Group from "@models/IPrice_Group";
import IGetCategoriesDTO from "@models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "@models/DTO/IOnGetCategoriesDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "@services/RestService";
import CatalogItemService from "@services/CatalogItemService";
import { useAppContextNonNull } from "@contexts/AppContext";
import "./BarcodeLabelSidebar.scss";

interface IBarcodeLabelItemUI {
    catalogItemGuid: string;
    quantity: number;
    name: string;
    sku: string;
    upc?: string;
}

type SelectionMode = "category" | "items";

const AVERY_TEMPLATES = [
    { value: AveryTemplateEnum.AVERY_5160, label: "Avery 5160", description: '30 labels/sheet — 2.625" x 1"', perSheet: 30 },
    { value: AveryTemplateEnum.AVERY_5163, label: "Avery 5163", description: '10 labels/sheet — 4" x 2"', perSheet: 10 },
    { value: AveryTemplateEnum.AVERY_5167, label: "Avery 5167", description: '80 labels/sheet — 1.75" x 0.5"', perSheet: 80 },
    { value: AveryTemplateEnum.AVERY_8163, label: "Avery 8163", description: '10 labels/sheet — 4" x 2"', perSheet: 10 },
];

const LS_KEY_TEMPLATE = "barcode_label_template";
const LS_KEY_BARCODE_SOURCE = "barcode_label_barcode_source";
const LS_KEY_PRICE_GROUP = "barcode_label_price_group";

type Props = {
    catalogGuid: string;
    catalogModel: ICatalog;
    priceGroups: IPrice_Group[];
};

export const BarcodeLabelSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    // Selection mode
    const [selectionMode, setSelectionMode] = useState<SelectionMode>("category");
    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined>(undefined);
    const [rootCategories, setRootCategories] = useState<ICategory[]>([]);

    // Items mode
    const [selectedItems, setSelectedItems] = useState<IBarcodeLabelItemUI[]>([]);
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);

    // Configuration
    const [template, setTemplate] = useState<AveryTemplateEnum>(AveryTemplateEnum.AVERY_5160);
    const [priceGroupGuid, setPriceGroupGuid] = useState<string>("");
    const [barcodeSource, setBarcodeSource] = useState<"sku" | "upc">("sku");
    const [showProductName, setShowProductName] = useState<boolean>(true);
    const [showPrice, setShowPrice] = useState<boolean>(false);
    const [quantityPerLabel, setQuantityPerLabel] = useState<number>(1);

    // Generation state
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Bulk quantity
    const [bulkQuantity, setBulkQuantity] = useState<number>(1);

    // Load saved settings from localStorage on mount
    useEffect(() => {
        const savedTemplate = localStorage.getItem(LS_KEY_TEMPLATE) as AveryTemplateEnum | null;
        if (savedTemplate && Object.values(AveryTemplateEnum).includes(savedTemplate)) {
            setTemplate(savedTemplate);
        }

        const savedSource = localStorage.getItem(LS_KEY_BARCODE_SOURCE) as "sku" | "upc" | null;
        if (savedSource === "sku" || savedSource === "upc") {
            setBarcodeSource(savedSource);
        }

        const savedPriceGroup = localStorage.getItem(LS_KEY_PRICE_GROUP);
        if (savedPriceGroup && props.priceGroups?.some((pg) => pg.guid === savedPriceGroup)) {
            setPriceGroupGuid(savedPriceGroup);
        } else if (props.priceGroups?.length) {
            setPriceGroupGuid(props.priceGroups[0].guid);
        }
    }, []);

    // Fetch root categories via socket
    useEffect(() => {
        const onGetCategories = (data: IOnGetCategoriesDTO) => {
            setRootCategories(data.categories.filter((c) => !c.hide_category));
        };

        socket.on(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);

        const payload: IGetCategoriesDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: props.catalogGuid,
            onlyRootCategories: true,
        };
        socket.emit(EndpointsEnum.CategoriesGetCategories, payload);

        return () => {
            socket.removeListener(EndpointsEnum.CategoriesOnGetCategories, onGetCategories);
        };
    }, [props.catalogGuid]);

    // Persist settings to localStorage
    useEffect(() => {
        localStorage.setItem(LS_KEY_TEMPLATE, template);
    }, [template]);

    useEffect(() => {
        localStorage.setItem(LS_KEY_BARCODE_SOURCE, barcodeSource);
    }, [barcodeSource]);

    useEffect(() => {
        if (priceGroupGuid) {
            localStorage.setItem(LS_KEY_PRICE_GROUP, priceGroupGuid);
        }
    }, [priceGroupGuid]);

    // Typeahead search
    const doTypeaheadSearch = useCallback(
        async (searchPhrase: string) => {
            if (!searchPhrase || searchPhrase.length < 2) {
                setTypeaheadItems([]);
                return;
            }
            try {
                const data = await CatalogItemService.typeaheadCatalogItems(props.catalogGuid, searchPhrase, userModel, supplierModel);
                setTypeaheadItems(data.catalog_items || []);
            } catch (err) {
                console.error("Error searching catalog items:", err);
                setTypeaheadItems([]);
            }
        },
        [props.catalogGuid, userModel, supplierModel]
    );

    // Add item (prevent duplicates)
    const addItem = useCallback(
        (item: ICatalog_Item) => {
            if (selectedItems.some((si) => si.catalogItemGuid === item.guid)) return;

            setSelectedItems((prev) => [
                ...prev,
                {
                    catalogItemGuid: item.guid,
                    quantity: 1,
                    name: item.name,
                    sku: item.sku,
                    upc: item.upc,
                },
            ]);
        },
        [selectedItems]
    );

    const updateItemQuantity = (catalogItemGuid: string, quantity: number) => {
        setSelectedItems((prev) =>
            prev.map((item) => (item.catalogItemGuid === catalogItemGuid ? { ...item, quantity: Math.max(0, quantity) } : item))
        );
    };

    const removeItem = (catalogItemGuid: string) => {
        setSelectedItems((prev) => prev.filter((item) => item.catalogItemGuid !== catalogItemGuid));
    };

    const applyBulkQuantity = () => {
        setSelectedItems((prev) => prev.map((item) => ({ ...item, quantity: bulkQuantity })));
    };

    // Summary calculations
    const labelsPerSheet = useMemo(() => {
        return AVERY_TEMPLATES.find((t) => t.value === template)?.perSheet || 30;
    }, [template]);

    const totalLabels = useMemo(() => {
        if (selectionMode === "items") {
            return selectedItems.reduce((sum, item) => sum + item.quantity, 0);
        }
        return 0;
    }, [selectionMode, selectedItems]);

    const estimatedPages = useMemo(() => {
        if (selectionMode === "items" && totalLabels > 0) {
            return Math.ceil(totalLabels / labelsPerSheet);
        }
        return null;
    }, [selectionMode, totalLabels, labelsPerSheet]);

    // Validation
    const canGenerate = useMemo(() => {
        if (!priceGroupGuid) return false;
        if (selectionMode === "category") return !!selectedCategory;
        return selectedItems.length > 0;
    }, [selectionMode, selectedCategory, selectedItems, priceGroupGuid]);

    // Generate PDF
    const generatePdf = async () => {
        setIsGenerating(true);
        setError(null);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.PdfBarcodeLabels,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const body: IBarcodeLabelDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                template,
                priceGroupGuid,
                catalogGuid: props.catalogGuid,
                barcodeSource,
                showProductName,
                showPrice,
                quantityPerLabel,
            };

            if (selectionMode === "category" && selectedCategory) {
                body.categoryGuid = selectedCategory.guid;
            } else if (selectionMode === "items" && selectedItems.length > 0) {
                body.items = selectedItems
                    .filter((item) => item.quantity > 0)
                    .map((item) => ({
                        catalogItemGuid: item.catalogItemGuid,
                        quantity: item.quantity,
                    }));
            }

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const pdfUrl = URL.createObjectURL(blob);
            window.open(pdfUrl, "_blank");
        } catch (err: any) {
            setError(err.message || "Failed to generate PDF");
            console.error("Error generating barcode labels:", err);
        } finally {
            setIsGenerating(false);
        }
    };

    // Selected category display name (supports hierarchy path)
    const selectedCategoryName = useMemo(() => {
        if (!selectedCategory) return "";
        if (selectedCategory.CategoryHierarchy?.length) {
            return selectedCategory.CategoryHierarchy.map((c) => c.name).join(" > ");
        }
        return selectedCategory.name;
    }, [selectedCategory]);

    return (
        <div className="BarcodeLabelSidebar d-flex flex-column p-3" style={{ height: "100vh", overflow: "hidden" }}>
            {/* Header */}
            <div>
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <OwTitle leftIconName="fad fa-barcode" title="barcode_labels" tag="h5" />
                    <OwButton centerIconClass="fas fa-times" fn={() => uiContext.toggleSidebar()} className="btn-danger" />
                </div>
                <hr className="mt-0" />
            </div>

            {/* Scrollable content */}
            <div className="flex-grow-1" style={{ overflowY: "auto" }}>
                {/* Selection Mode Toggle */}
                <div className="mb-3">
                    <div className="btn-group w-100" role="group">
                        <button
                            className={`btn btn-sm ${selectionMode === "category" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setSelectionMode("category")}>
                            By Category
                        </button>
                        <button
                            className={`btn btn-sm ${selectionMode === "items" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setSelectionMode("items")}>
                            By Specific Items
                        </button>
                    </div>
                </div>

                {/* Category Mode */}
                {selectionMode === "category" && (
                    <div className="mb-3">
                        <OwHierarchicalCategorySelector
                            categories={rootCategories}
                            value={selectedCategory}
                            onChange={(category) => setSelectedCategory(category)}
                            placeholder="select_category"
                            userModel={userModel}
                            supplierModel={supplierModel}
                        />
                        {selectedCategory && (
                            <small className="text-muted mt-1 d-block">
                                All items in &quot;{selectedCategoryName}&quot; will be included (1 label each)
                            </small>
                        )}
                    </div>
                )}

                {/* Items Mode */}
                {selectionMode === "items" && (
                    <div className="mb-3">
                        <TypeaheadFn
                            lookupFn={doTypeaheadSearch}
                            availableOptions={typeaheadItems}
                            selectedOptionFn={addItem}
                            emitSelectionSetup={{ emitObject: true }}
                            titleProp="name"
                            subTitleProp="sku"
                            confirmAdd={true}
                            placeholder="Search by name or SKU..."
                        />

                        {/* Bulk Quantity */}
                        {selectedItems.length > 0 && (
                            <div className="d-flex align-items-center mt-2 mb-2">
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    style={{ width: "80px" }}
                                    value={bulkQuantity}
                                    min={1}
                                    onChange={(e) => setBulkQuantity(parseInt(e.target.value) || 1)}
                                />
                                <OwButton fn={applyBulkQuantity} title="apply_to_all" className="btn-outline-primary ms-2" />
                                <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => setSelectedItems([])}>
                                    Clear All
                                </button>
                            </div>
                        )}

                        {/* Selected Items List */}
                        <div className="selected-items-list mt-2">
                            {selectedItems.map((item) => (
                                <div key={item.catalogItemGuid} className="item-row d-flex align-items-center mb-2 p-2 border rounded">
                                    <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                                        <div className="fw-bold small text-truncate">{item.name}</div>
                                        <small className="text-muted">
                                            {item.sku}
                                            {item.upc ? ` | ${item.upc}` : ""}
                                        </small>
                                        {barcodeSource === "upc" && !item.upc && (
                                            <span className="upc-warning ms-1" title="No UPC — will fall back to SKU">
                                                <i className="fas fa-exclamation-triangle" />
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        style={{ width: "65px" }}
                                        value={item.quantity}
                                        min={0}
                                        onChange={(e) => updateItemQuantity(item.catalogItemGuid, parseInt(e.target.value) || 0)}
                                    />
                                    <button className="btn btn-outline-danger btn-sm ms-1" onClick={() => removeItem(item.catalogItemGuid)}>
                                        <i className="fas fa-times" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <hr />

                {/* Template Selector */}
                <div className="mb-3">
                    <OwLabel title="template" />
                    {AVERY_TEMPLATES.map((t) => (
                        <div key={t.value} className="form-check mb-1">
                            <input
                                className="form-check-input"
                                type="radio"
                                name="template"
                                id={`template-${t.value}`}
                                value={t.value}
                                checked={template === t.value}
                                onChange={() => setTemplate(t.value)}
                            />
                            <label className="form-check-label" htmlFor={`template-${t.value}`}>
                                <span className="fw-bold">{t.label}</span>
                                <br />
                                <small className="text-muted">{t.description}</small>
                            </label>
                        </div>
                    ))}
                </div>

                {/* Barcode Source */}
                <div className="mb-3">
                    <OwLabel title="barcode_source" />
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="barcodeSource"
                            id="barcodeSource-sku"
                            value="sku"
                            checked={barcodeSource === "sku"}
                            onChange={() => setBarcodeSource("sku")}
                        />
                        <label className="form-check-label" htmlFor="barcodeSource-sku">
                            SKU
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="barcodeSource"
                            id="barcodeSource-upc"
                            value="upc"
                            checked={barcodeSource === "upc"}
                            onChange={() => setBarcodeSource("upc")}
                        />
                        <label className="form-check-label" htmlFor="barcodeSource-upc">
                            UPC
                        </label>
                    </div>
                    {barcodeSource === "upc" && selectionMode === "items" && selectedItems.some((i) => !i.upc) && (
                        <small className="text-warning d-block mt-1">
                            <i className="fas fa-exclamation-triangle me-1" />
                            Some items have no UPC and will fall back to SKU
                        </small>
                    )}
                </div>

                {/* Display Options */}
                <div className="mb-2">
                    <OwToggle
                        fn={() => setShowProductName((prev) => !prev)}
                        name="showProductName"
                        label="show_product_name"
                        active={showProductName}
                    />
                </div>
                <div className="mb-3">
                    <OwToggle fn={() => setShowPrice((prev) => !prev)} name="showPrice" label="show_price" active={showPrice} />

                    {showPrice && (
                        <div className="mt-2 mb-3">
                            <OwLabel title="price_group" />
                            <OwSelectOption
                                name="priceGroupGuid"
                                options={props.priceGroups}
                                valueProp="guid"
                                titleProp="value"
                                fn={(e: ChangeEvent<HTMLSelectElement>) => setPriceGroupGuid(e.target.value)}
                                selectedOption={priceGroupGuid}
                                title="price_group"
                                translateTitle={false}
                            />
                        </div>
                    )}
                </div>

                {/* Quantity Per Label */}
                <div className="mb-3">
                    <OwLabel title="quantity_per_label" />
                    <OwFnInput
                        fn={(e: ChangeEvent<HTMLInputElement>) => setQuantityPerLabel(Math.max(1, parseInt(e.target.value) || 1))}
                        name="quantityPerLabel"
                        value={quantityPerLabel}
                        type="number"
                        min={1}
                        isSmall={true}
                    />
                </div>

                <hr />

                {/* Summary */}
                <div className="mb-3">
                    {selectionMode === "items" && selectedItems.length > 0 && (
                        <div className="small">
                            <div>
                                Total labels: <strong>{totalLabels}</strong>
                            </div>
                            <div>
                                Labels per sheet: <strong>{labelsPerSheet}</strong>
                            </div>
                            {estimatedPages !== null && (
                                <div>
                                    Estimated pages: <strong>{estimatedPages}</strong>
                                </div>
                            )}
                        </div>
                    )}
                    {selectionMode === "category" && selectedCategory && (
                        <div className="small">
                            All items in &quot;<strong>{selectedCategoryName}</strong>&quot; x 1 each
                        </div>
                    )}
                </div>

                {/* Error display */}
                {error && <div className="alert alert-danger small py-2">{error}</div>}
            </div>

            {/* Footer: Generate Button */}
            <div className="pt-3 border-top">
                <OwButton
                    fn={generatePdf}
                    title="generate_labels"
                    leftIconClass="fad fa-file-pdf"
                    className="btn-primary w-100"
                    disabled={!canGenerate || isGenerating}
                    active={isGenerating}
                    disableOnActive={true}
                />
            </div>
        </div>
    );
};
